import { CanvasTexture, DataTexture, NoColorSpace, RepeatWrapping, RGBAFormat, SRGBColorSpace, Texture, TextureLoader } from 'three/webgpu';
import { generateFireSheetCanvas, generateSmokeSheetCanvas, generateTextureCanvas } from './textureGen.js';
import { resolveAsset } from './assets.js';
/**
 * Built-in starter sprites (plan §2.7), generated procedurally at first use so
 * effect JSON that references them is portable with zero asset files.
 * All are white-on-transparent 128² canvases — color comes from tint/gradient.
 */
const SIZE = 128;
const cache = new Map();
/** Headless (Node/test/SSR) fallback: a plain white texture instead of a canvas render. */
let whiteTex = null;
function whiteTexture() {
    if (!whiteTex) {
        whiteTex = new DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, RGBAFormat);
        whiteTex.needsUpdate = true;
    }
    return whiteTex;
}
function makeCanvas(draw) {
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, SIZE, SIZE);
    draw(ctx);
    const tex = new CanvasTexture(canvas);
    tex.colorSpace = SRGBColorSpace;
    return tex;
}
function radial(stops) {
    return (ctx) => {
        const g = ctx.createRadialGradient(SIZE / 2, SIZE / 2, 0, SIZE / 2, SIZE / 2, SIZE / 2);
        for (const [offset, alpha] of stops)
            g.addColorStop(offset, `rgba(255,255,255,${alpha})`);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, SIZE, SIZE);
    };
}
const BUILTINS = {
    softDot: () => makeCanvas(radial([
        [0, 1],
        [0.25, 0.85],
        [0.6, 0.3],
        [1, 0],
    ])),
    hardDot: () => makeCanvas(radial([
        [0, 1],
        [0.78, 1],
        [0.88, 0.9],
        [0.95, 0],
        [1, 0],
    ])),
    glow: () => makeCanvas(radial([
        [0, 1],
        [0.12, 0.8],
        [0.3, 0.35],
        [0.55, 0.12],
        [1, 0],
    ])),
    ring: () => makeCanvas(radial([
        [0, 0],
        [0.55, 0],
        [0.68, 1],
        [0.8, 1],
        [0.95, 0],
        [1, 0],
    ])),
    streak: () => makeCanvas((ctx) => {
        // Elongated horizontal streak: soft falloff along X, tight gaussian-ish across Y.
        const img = ctx.createImageData(SIZE, SIZE);
        for (let y = 0; y < SIZE; y++) {
            const ny = (y / (SIZE - 1)) * 2 - 1;
            const ay = Math.exp(-ny * ny * 18);
            for (let x = 0; x < SIZE; x++) {
                const nx = (x / (SIZE - 1)) * 2 - 1;
                const ax = Math.max(0, 1 - Math.abs(nx));
                const a = Math.pow(ax, 0.7) * ay;
                const i = (y * SIZE + x) * 4;
                img.data[i] = img.data[i + 1] = img.data[i + 2] = 255;
                img.data[i + 3] = Math.round(a * 255);
            }
        }
        ctx.putImageData(img, 0, 0);
    }),
    star: () => makeCanvas((ctx) => {
        // 4-point sparkle: two crossed concave spikes.
        const c = SIZE / 2;
        ctx.fillStyle = 'rgba(255,255,255,1)';
        const spike = (angle) => {
            ctx.save();
            ctx.translate(c, c);
            ctx.rotate(angle);
            ctx.beginPath();
            ctx.moveTo(0, -c * 0.98);
            ctx.quadraticCurveTo(c * 0.08, -c * 0.08, c * 0.98, 0);
            ctx.quadraticCurveTo(c * 0.08, c * 0.08, 0, c * 0.98);
            ctx.quadraticCurveTo(-c * 0.08, c * 0.08, -c * 0.98, 0);
            ctx.quadraticCurveTo(-c * 0.08, -c * 0.08, 0, -c * 0.98);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
        };
        spike(0);
        ctx.globalAlpha = 0.9;
        spike(Math.PI / 4);
        // soft core
        const g = ctx.createRadialGradient(c, c, 0, c, c, c * 0.3);
        g.addColorStop(0, 'rgba(255,255,255,1)');
        g.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.globalAlpha = 1;
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, SIZE, SIZE);
    }),
    noise: () => {
        // seamless grayscale mask (dissolve, distortion) — repeat wrapping, linear data
        const tex = new CanvasTexture(generateTextureCanvas({ kind: 'noise', noise: 'value', cells: 6, seed: 11 }, 256));
        tex.wrapS = RepeatWrapping;
        tex.wrapT = RepeatWrapping;
        tex.colorSpace = NoColorSpace;
        return tex;
    },
    smokeSheet: () => {
        const tex = new CanvasTexture(generateSmokeSheetCanvas(512, 16, 4, 7));
        tex.colorSpace = SRGBColorSpace;
        return tex;
    },
    fireSheet: () => {
        const tex = new CanvasTexture(generateFireSheetCanvas(512, 16, 4, 3));
        tex.colorSpace = SRGBColorSpace;
        return tex;
    },
};
export const BUILTIN_SPRITE_IDS = Object.keys(BUILTINS);
export function builtinSprite(id) {
    if (typeof document === 'undefined')
        return whiteTexture();
    let tex = cache.get(id);
    if (!tex) {
        tex = BUILTINS[id]();
        cache.set(id, tex);
    }
    return tex;
}
let loader = null;
/** Resolve a SpriteRef to a texture, or null for the procedural (in-shader) sprite. */
export function resolveSpriteTexture(ref) {
    switch (ref.type) {
        case 'procedural':
            return null;
        case 'builtin':
            return builtinSprite(ref.id);
        case 'dataUri':
            return urlTexture(ref.uri);
        case 'asset': {
            const resolved = resolveAsset(ref.id);
            if (!resolved)
                return whiteTexture();
            if (typeof resolved !== 'string')
                return resolved; // pre-loaded Texture
            return urlTexture(resolved);
        }
    }
}
function urlTexture(url) {
    if (typeof document === 'undefined')
        return whiteTexture();
    let tex = cache.get(url);
    if (!tex) {
        loader ??= new TextureLoader();
        tex = loader.load(url);
        tex.colorSpace = SRGBColorSpace;
        cache.set(url, tex);
    }
    return tex;
}
